import CameraAltRounded from '@mui/icons-material/CameraAltRounded';
import EmailRounded from '@mui/icons-material/EmailRounded';
import LinkedCameraRounded from '@mui/icons-material/LinkedCameraRounded';
import UploadRounded from '@mui/icons-material/UploadRounded';
import { Dispatch, SetStateAction } from 'react';
import { createSearchParams } from 'react-router';

import {
  AssignmentStatus,
  CaptureMethod,
  Piece,
  PieceStatus,
  PieceType,
  RdConnectInstancePingResponse,
} from '@apiContract';
import { mailroomRoutes } from '@routes';
import { AxiosResponse } from 'axios';
import { ReceiveToggleButtonGroupConfig } from 'src/types/shared';
import { v4 } from 'uuid';

import { RdConnectPingListParams, getRdConnectInstancePing } from '@src/api/automatedCapture';
import { PackageIcon } from '@src/assets/icons/PackageIcon';
import { getCompanyFromHost } from '@utils/url';

import {
  ButtonTypes,
  CaptureId,
  CapturePiece,
  ColorStatus,
  IconColors,
  OnFileAddParams,
  RecipientStatus,
  Steps,
} from './types';

export const tooltips = {
  PersonPinRounded: 'Assign',
  PersonPinRoundedLoading: 'Identifying recipient',
  LocationOnRounded: 'Edit Storage Location',
  AddCommentRounded: 'Add Comment',
  CropRotateRounded: 'Crop and Rotate',
  Barcode: 'Edit Tracking Numbers',
  DeleteOutlined: 'Delete',
  Rotate90Degrees: 'Rotate 90 degrees',
  CloudUploadRounded: 'Upload',
  Print: 'Print Shipping Label',
  AdditionalInfo: 'Additional Info',
};

export enum RecipientMessages {
  identifiedSuccessfully = 0,
  previousAssignment = 1,
  requiresValidation = 2,
  possibleMatches = 3,
  identifiedNotExist = 4,
  unableToIdentify = 5,
  identifiedNotAssigned = 6,
  refresh = 7,
}

export const recipientAlerts = {
  [RecipientMessages.identifiedSuccessfully]: {
    text: 'Recipient identified successfully',
    type: 'success',
  },
  [RecipientMessages.previousAssignment]: {
    text: 'Recipient identified based on previous assignment',
    type: 'success',
  },
  [RecipientMessages.requiresValidation]: {
    text: 'Recipient identified but requires validation',
    type: 'warning',
  },
  [RecipientMessages.possibleMatches]: {
    text: 'Possible recipient matches identified',
    type: 'error',
  },
  [RecipientMessages.identifiedNotExist]: {
    text: 'Recipient identified but does not exist',
    type: 'error',
  },
  [RecipientMessages.unableToIdentify]: {
    text: 'Unable to identify recipient',
    type: 'error',
  },
  [RecipientMessages.identifiedNotAssigned]: {
    text: 'Recipient identified but is not assigned',
    type: 'error',
  },
  [RecipientMessages.refresh]: {
    text: 'Recipient identified automatically',
    type: 'info',
  },
};

type OnClickType = (prop?: any) => void;

export const getButtonConfig = (
  label: string,
  type: ButtonTypes,
  onClick: OnClickType = () => {},
  disabled = false,
  options: { captureDropdown?: boolean } = {},
) => ({
  label,
  type,
  onClick,
  disabled,
  ...options,
});

export const getIconConfig = (
  Icon: React.ElementType,
  tooltip: string,
  isLoading: boolean,
  onClick: (params?: any) => void,
  options: {
    color?: IconColors;
    disabled?: boolean;
    tooltip?: string;
    deleteIcon?: boolean;
    id?: CaptureId | null;
  } = {},
) => ({
  Icon,
  isLoading,
  onClick,
  color: options.color,
  tooltip: options.tooltip || tooltips[tooltip as keyof typeof tooltips],
  disabled: options.disabled,
  deleteIcon: !!options.deleteIcon,
  options,
});

export const getUserIconTooltip = (isLoading: boolean) =>
  tooltips[isLoading ? 'PersonPinRoundedLoading' : 'PersonPinRounded'];

export const getRecipientStatus = (piece?: Piece | null) => {
  if (piece?.pieceSummary?.pieceAssignmentStatus === AssignmentStatus.Assigned) {
    return RecipientStatus.ready;
  }

  if (piece?.pieceSummary?.pieceAssignmentStatus === AssignmentStatus.RequireValidation) {
    return RecipientStatus.review;
  }

  return RecipientStatus.invalid;
};

export const getUserColorPieceStatus = (piece?: Piece | null) => {
  const recipientStatus = getRecipientStatus(piece);
  if (recipientStatus) {
    return ColorStatus[recipientStatus];
  }

  return;
};

export const parseUploadFiles = (uploadedFiles: Array<File | string>, sessionId: string) => {
  return uploadedFiles.reduce((acc: Array<OnFileAddParams>, file) => {
    const id = v4();
    acc.push({ file, fileId: id, sessionId, host: getCompanyFromHost() });
    return acc;
  }, []);
};

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const captureMethodOptions = (isShowAutomated: boolean): ReceiveToggleButtonGroupConfig<CaptureMethod>[] => {
  return [
    {
      value: CaptureMethod.Single,
      label: 'Single',
      Icon: CameraAltRounded,
    },
    {
      value: CaptureMethod.Automated,
      label: 'Automated',
      Icon: LinkedCameraRounded,
      disabled: !isShowAutomated,
      tooltip: 'Contact sales to enable Automated capture',
    },
    {
      value: CaptureMethod.Images,
      label: 'Upload images',
      Icon: UploadRounded,
    },
  ];
};

export const itemTypeOptions: ReceiveToggleButtonGroupConfig<PieceType>[] = [
  {
    value: PieceType.Mail,
    label: 'Mail',
    Icon: EmailRounded,
  },
  {
    value: PieceType.Package,
    label: 'Packages',
    Icon: PackageIcon,
  },
];

export const nextStepReceiveConfig = {
  [CaptureMethod.Single]: Steps.Photo,
  [CaptureMethod.Automated]: Steps.AutomatedCapture,
  [CaptureMethod.Images]: Steps.Images,
};

export const settingsNames = [
  'ReceiveMailWorkflow',
  'ReceivePackagesWorkflow',
  'ReceiveSortOrder',
  'LockersWorkflowEnable',
  'DefaultItemType',
  'LabelPrintingEnabled',
  'DefaultAutoPrintLabelOnAssignment',
];

export const waitCameraForIdleState = async (
  params: RdConnectPingListParams,
  setAutomatedCaptureInProgress: Dispatch<SetStateAction<boolean>>,
): Promise<AxiosResponse<RdConnectInstancePingResponse>> => {
  return new Promise((resolve, reject) => {
    const interval = 1000;
    // eslint-disable-next-line prefer-const
    let timer: NodeJS.Timeout;

    const checkState = async () => {
      try {
        const response = await getRdConnectInstancePing(params);
        if (response.data.objectData.appState === 'Idle') {
          clearInterval(timer);
          setAutomatedCaptureInProgress(false);
          resolve(response);
        }
      } catch (error) {
        clearInterval(timer);
        reject(error);
      }
    };

    timer = setInterval(checkState, interval);
  });
};

export const getFinishCaptureNavigateProps = (userId: string) => ({
  pathname: mailroomRoutes.items,
  search: createSearchParams({
    PieceStatus: PieceStatus.Unsent.toString(),
    receivedBy: [userId],
  }).toString(),
});

export const isPieceLoading = (capturePiece: CapturePiece) => !capturePiece.piece && capturePiece.status !== 'failed';
